/**
 * CombinedMonteCarloEngine.ts
 *
 * Engine for running combined Monte Carlo simulations comparing 3-Topf and 1-Topf strategies
 */

import { OneTopfSimulationOptions, SteuerParameter } from '../types';
import { ThreeTopfSimulationEngine } from './ThreeTopfSimulationEngine';
import {
  CombinedOptimizationConfig,
  CombinedOptimizationResult,
  CrisisDefinitionType,
  ScenarioCellResult,
  ScenarioMatrixConfig,
  ThreeTopfSimulationOptions as SimulationOptions,
  WithdrawalRateComparisonConfig,
  WithdrawalRateComparisonResult,
  ZinstopfComparisonConfig,
  ZinstopfComparisonResult,
} from '../types/simulation';

import { OneTopfSimulationEngine } from './OneTopfSimulationEngine';
// SharedRateGenerator will be loaded dynamically
import { StatisticalHelpers } from '../utils/statisticalHelpers';
import { isStrategySuccessful, getTargetYearFundingRatio } from '../utils/successCriteria';
import { INFLATION_OPTIONS, INVESTMENT_RETURN_OPTIONS } from '../utils/optionLabels';
import { buildScenarioCellResult } from '../utils/scenarioVerdict';

export interface CombinedMonteCarloResult {
  iteration: number;
  // 3-Topf results
  threeTopf: {
    finalBalance: number;
    totalWithdrawals: number;
    successfulStrategy: boolean;
    yearlyBalances: number[]; // Total portfolio balance per year
    kapitalIndex: number[]; // Kapitalindex values per year (starting at 100)
    jahresergebnisse: import('../types/simulation').ThreeTopfYearData[]; // Full year-by-year data - SINGLE SOURCE OF TRUTH
  };

  // 1-Topf results
  oneTopf: {
    finalBalance: number;
    totalWithdrawals: number;
    successfulStrategy: boolean;
    yearlyBalances: number[]; // Total portfolio balance per year
    kapitalIndex: number[]; // Kapitalindex values per year (starting at 100)
    jahresergebnisse: import('../types').OneTopfYearData[]; // Full year-by-year data - SINGLE SOURCE OF TRUTH
  };

  // Failure comparison metrics
  failureAnalysis: {
    threeTopfFailedFirst: boolean; // True if 3-Topf depleted before 1-Topf
    oneTopfFailedFirst: boolean; // True if 1-Topf depleted before 3-Topf
    yearsAdvantage: number; // How many years one strategy lasted longer than the other
    earlyFailureType: 'none' | '3-topf-early' | '1-topf-early'; // Classification of failure type
  };
}

export interface CombinedMonteCarloStats {
  iterations: number;

  // 3-Topf statistics
  threeTopf: {
    averageFinalBalance: number;
    medianFinalBalance: number;
    averageYearsFunded: number;
    successRate: number; // % of iterations that succeeded
    percentile5: number;
    percentile95: number;
  };

  // 1-Topf statistics
  oneTopf: {
    averageFinalBalance: number;
    medianFinalBalance: number;
    averageYearsFunded: number;
    successRate: number; // % of iterations that succeeded
    percentile5: number;
    percentile95: number;
  };

  // Comparison metrics
  comparison: {
    threeTopfAdvantageRate: number; // % of iterations where 3-Topf was better
    oneTopfAdvantageRate: number; // % of iterations where 1-Topf was better
    averageBalanceDifference: number; // Average difference in final balance (3-Topf vs 1-Topf)
    bestCombinedStrategy: string; // Which strategy performed best overall
  };

  // Critical failure analysis
  failureAnalysis: {
    threeTopfEarlyFailureRate: number; // % of iterations where 3-Topf failed before 1-Topf
    oneTopfEarlyFailureRate: number; // % of iterations where 1-Topf failed before 3-Topf
    bothFailedRate: number; // % where both strategies failed
    averageYearsAdvantage: number; // Average years one strategy outlasted the other
    worstCaseScenarios: {
      threeTopfWorst: CombinedMonteCarloResult[]; // Top 10 worst 3-Topf early failures
      oneTopfWorst: CombinedMonteCarloResult[]; // Top 10 worst 1-Topf early failures
    };
    criticalFailureThreshold: number; // Years difference threshold for "critical" failures
    failureComparison: {
      threeTopfCriticalFailures: number; // Count of 3-Topf failures beyond threshold
      oneTopfCriticalFailures: number; // Count of 1-Topf failures beyond threshold
      maxYearsAdvantage: {
        threeTopf: number; // Maximum years 3-Topf outlasted 1-Topf
        oneTopf: number; // Maximum years 1-Topf outlasted 3-Topf
      };
      averageYearsAdvantage: {
        threeTopf: number; // Average years 3-Topf outlasted 1-Topf (when 1-Topf failed first)
        oneTopf: number; // Average years 1-Topf outlasted 3-Topf (when 3-Topf failed first)
      };
    };
  };
}

export class CombinedMonteCarloEngine {
  private steuerParameter: SteuerParameter;
  private threeTopfOptions: SimulationOptions;
  private oneTopfOptions: OneTopfSimulationOptions;
  private crisisDefinition: CrisisDefinitionType;
  private sharedRateGenerator: any = null;
  private storedResults: CombinedMonteCarloResult[] = []; // Store original Monte Carlo results

  constructor(
    steuerParameter: SteuerParameter,
    threeTopfOptions: SimulationOptions,
    oneTopfOptions: OneTopfSimulationOptions,
    crisisDefinition: CrisisDefinitionType = 'negative-return'
  ) {
    this.steuerParameter = steuerParameter;
    this.threeTopfOptions = threeTopfOptions;
    this.oneTopfOptions = oneTopfOptions;
    this.crisisDefinition = crisisDefinition;
    this.initializeSharedRateGenerator();
  }

  private async initializeSharedRateGenerator(): Promise<void> {
    try {
      const { SharedRateGenerator } = await import('./SharedRateGenerator');
      this.sharedRateGenerator = new SharedRateGenerator(this.steuerParameter, {
        inflationOption: this.threeTopfOptions.inflationOption,
        investmentReturnOption: this.threeTopfOptions.investmentReturnOption,
        tagesgeldOption: this.threeTopfOptions.tagesgeldOption,
        zinstopfOption: this.threeTopfOptions.zinstopfOption,
      });
    } catch (error) {
      console.error('Failed to load SharedRateGenerator:', error);
    }
  }

  /**
   * Run combined Monte Carlo simulation
   * @param storeIterationDetails - If false, skips storing iteration details to save memory (useful for Zinstopf optimization)
   */
  async runCombinedMonteCarlo(
    iterations: number,
    progressCallback?: (progress: number) => void | Promise<void>,
    storeIterationDetails: boolean = true
  ): Promise<CombinedMonteCarloResult[]> {
    const results: CombinedMonteCarloResult[] = [];
    const entnahmeJahre =
      this.steuerParameter.berechnungBisLebensalter - this.steuerParameter.retirementAge;

    // Performance optimization: Warm up GARCH caches before starting Monte Carlo
    if (this.threeTopfOptions.investmentReturnOption?.includes('garch') || 
        this.oneTopfOptions.investmentReturnOption?.includes('garch')) {
      const { GARCHCacheUtils } = await import('../utils/garchModeling');
      GARCHCacheUtils.warmupCaches();
    }

    for (let i = 0; i < iterations; i++) {
      try {
        // Ensure sharedRateGenerator is initialized
        if (!this.sharedRateGenerator) {
          await this.initializeSharedRateGenerator();
        }

        // Generate shared rates for this iteration
        const sharedRates = await this.sharedRateGenerator.generateRatesForIteration(
          entnahmeJahre,
          i
        );

        // Run 3-Topf iteration (creates fresh engine instance)
        const threeTopfResult = await this.runSingle3TopfIteration(i, entnahmeJahre, sharedRates);

        // Run 1-Topf iteration (creates fresh engine instance)
        const oneTopfResult = await this.runSingle1TopfIteration(i, entnahmeJahre, sharedRates);

        // Calculate failure analysis for this iteration
        const failureAnalysis = this.calculateFailureAnalysis(i, threeTopfResult, oneTopfResult);

        const iterationResult = {
          iteration: i,
          threeTopf: threeTopfResult,
          oneTopf: oneTopfResult,
          failureAnalysis,
        };

        results.push(iterationResult);
        
        // Store result for later detailed analysis (without re-simulation)
        // Skip storing if storeIterationDetails is false (saves memory for Zinstopf optimization)
        if (storeIterationDetails) {
          this.storedResults.push(iterationResult);
        }

        // Update progress more frequently for smoother UI updates
        if (progressCallback) {
          const progress = ((i + 1) / iterations) * 100;
          
          // Update progress every 5 iterations for small simulations, every 10 for larger ones
          const updateFrequency = iterations <= 100 ? 5 : 10;
          
          if ((i + 1) % updateFrequency === 0 || (i + 1) === iterations) {
            await progressCallback(progress);
          }

          // Add small delay every 100 iterations to make progress visible and prevent UI freezing
          if ((i + 1) % 100 === 0) {
            await new Promise(resolve => setTimeout(resolve, 25));
          }
        }
      } catch (error) {
        console.error(`Error in iteration ${i}:`, error);
        // Continue with next iteration
      }
    }

    // Ensure final progress callback at 100% - this guarantees the progress bar reaches 100%
    if (progressCallback) {
      await progressCallback(100);
    }

    return results;
  }

  /**
   * Run a single 3-Topf iteration with shared rates
   */
  private async runSingle3TopfIteration(
    _iteration: number, // Unused for now
    entnahmeJahre: number,
    sharedRates: any
  ): Promise<{
    finalBalance: number;
    totalWithdrawals: number;
    successfulStrategy: boolean;
    yearlyBalances: number[];
    kapitalIndex: number[];
    jahresergebnisse: import('../types/simulation').ThreeTopfYearData[];
  }> {
    // Create a SimulationEngine with shared rates instead of using MonteCarloEngine
    const simulationEngine = new ThreeTopfSimulationEngine(this.steuerParameter, this.threeTopfOptions, this.crisisDefinition);
    simulationEngine.setSharedRates(sharedRates);

    // Run simulation
    const results = await simulationEngine.runSimulation();
    const lastYear = results[results.length - 1];

    // Calculate metrics
    const totalWithdrawals = results.reduce((sum, year) => sum + year.gesamtentnahme, 0);
    const finalBalance = lastYear ? lastYear.gesamtsaldo : 0;
    // Erfolg (verfeinerte Definition, zentral in isStrategySuccessful):
    // Portfolio erreicht das Zielalter (length >= entnahmeJahre + 1) UND bedient im
    // Zieljahr mindestens SUCCESS_WITHDRAWAL_THRESHOLD der inflationsadjustierten
    // Zielentnahme. Ein Lauf, der das Zielalter zwar erreicht, dort aber nur eine
    // Teil-Entnahme bedienen konnte (Saldo leergelaufen), zählt jetzt als Misserfolg.
    const successfulStrategy = isStrategySuccessful(results, entnahmeJahre);

    // Capture yearly total balances (sum of all Töpfe)
    const yearlyBalances = results.map(year => year.gesamtsaldo);

    // Use actual Kapitalindex from simulation results (ensures consistency with Einzelsimulation)
    const kapitalIndex = results.map(year => {
      // Use the actual kapitalIndex from simulation data, with fallback for backward compatibility
      if (year.kapitalIndex !== undefined) {
        return Math.round(year.kapitalIndex);
      }
      
      // Fallback: Calculate if not available (should not happen with current implementation)
      console.warn('Monte Carlo 3-Topf: kapitalIndex missing from simulation result, using fallback calculation');
      return 100; // Safe default
    });

    return {
      finalBalance,
      totalWithdrawals,
      successfulStrategy,
      yearlyBalances,
      kapitalIndex,
      jahresergebnisse: results, // SINGLE SOURCE OF TRUTH - all other metrics derived from this
    };
  }

  /**
   * Get detailed year-by-year data for a specific iteration
   * Re-runs the simulation with the same random seed/parameters
   * 
   * ⚠️ DEPRECATED: This method re-simulates iterations and produces NON-DETERMINISTIC results
   * due to GARCH sequence regeneration, cache timing, and floating point precision issues.
   * 
   * ✅ RECOMMENDED: Use getStoredIterationResult() instead to get the authoritative data
   * from the original Monte Carlo run. This ensures 100% consistency with table/export data.
   * 
   * This method should ONLY be used for debugging or when stored results are unavailable.
   * The original Monte Carlo depletion ages stored in jahresergebnisse are authoritative.
   * 
   * @deprecated Use getStoredIterationResult() for consistent, authoritative data
   */
  async getIterationDetails(iteration: number): Promise<{
    threeTopf: import('../types/simulation').ThreeTopfYearData[];
    oneTopf: import('../types').OneTopfYearData[];
    sharedRates?: any;
    warning?: string;
  }> {
    // Ensure SharedRateGenerator is initialized
    if (!this.sharedRateGenerator) {
      await this.initializeSharedRateGenerator();
      if (!this.sharedRateGenerator) {
        throw new Error('Failed to initialize SharedRateGenerator. Cannot re-simulate iteration.');
      }
    }

    const entnahmeJahre = this.steuerParameter.berechnungBisLebensalter - this.steuerParameter.retirementAge;
    
    // Generate the same shared rates using the iteration as seed
    // This ensures we get the exact same market scenario as the original simulation
    const sharedRates = await this.sharedRateGenerator.generateRatesForIteration(entnahmeJahre, iteration);

    // Import and create fresh simulation engines with the shared rates
    const { ThreeTopfSimulationEngine } = await import('./ThreeTopfSimulationEngine');
    const { OneTopfSimulationEngine } = await import('./OneTopfSimulationEngine');
    
    const threeTopfEngine = new ThreeTopfSimulationEngine(this.steuerParameter, this.threeTopfOptions, this.crisisDefinition);
    const oneTopfEngine = new OneTopfSimulationEngine(this.steuerParameter, this.oneTopfOptions, this.crisisDefinition);

    // Set shared rates for both engines
    threeTopfEngine.setSharedRates(sharedRates);
    oneTopfEngine.setSharedRates(sharedRates);

    // Run both simulations to get complete year-by-year data
    const [threeTopfResults, oneTopfResults] = await Promise.all([
      threeTopfEngine.runSimulation(),
      oneTopfEngine.runSimulation()
    ]);

    // Calculate depletion ages from re-simulation for comparison
    let threeTopfDepletionAge: number | undefined;
    let oneTopfDepletionAge: number | undefined;

    for (let i = 0; i < threeTopfResults.length; i++) {
      if (threeTopfResults[i].gesamtsaldo <= 0) {
        threeTopfDepletionAge = this.steuerParameter.retirementAge + i;
        break;
      }
    }

    for (let i = 0; i < oneTopfResults.length; i++) {
      if (oneTopfResults[i].gesamtsaldo <= 0) {
        oneTopfDepletionAge = this.steuerParameter.retirementAge + i;
        break;
      }
    }

    // Add warning if re-simulation produces different depletion ages than original
    let warning: string | undefined;
    const storedResult = this.getStoredIterationResult(iteration);
    
    if (storedResult) {
      // Use jahresergebnisse as SINGLE SOURCE OF TRUTH for comparison
      const originalThreeTopfAge = storedResult.threeTopf.jahresergebnisse[storedResult.threeTopf.jahresergebnisse.length - 1].alter;
      const originalOneTopfAge = storedResult.oneTopf.jahresergebnisse[storedResult.oneTopf.jahresergebnisse.length - 1].alter;
      
      console.log(`[DEBUG] Iteration ${iteration} comparison:`);
      console.log(`  Original: 3-Topf=${originalThreeTopfAge}, 1-Topf=${originalOneTopfAge}`);
      console.log(`  Re-simulation: 3-Topf=${threeTopfDepletionAge}, 1-Topf=${oneTopfDepletionAge}`);

      // Only warn if there's a REAL difference: both defined AND different
      const threeTopfChanged = originalThreeTopfAge !== undefined &&
        threeTopfDepletionAge !== undefined &&
        originalThreeTopfAge !== threeTopfDepletionAge;
      const oneTopfChanged = originalOneTopfAge !== undefined &&
        oneTopfDepletionAge !== undefined &&
        originalOneTopfAge !== oneTopfDepletionAge;

      if (threeTopfChanged || oneTopfChanged) {
        warning = `⚠️ Re-simulation produced different depletion ages. Original: 3-Topf=${originalThreeTopfAge ?? 'survived'}, 1-Topf=${originalOneTopfAge ?? 'survived'}. Re-sim: 3-Topf=${threeTopfDepletionAge ?? 'survived'}, 1-Topf=${oneTopfDepletionAge ?? 'survived'}. This may be due to floating-point rounding or GARCH sequence regeneration.`;
        console.warn(`[WARNING] ${warning}`);
      }
    }
    
    return {
      threeTopf: threeTopfResults,
      oneTopf: oneTopfResults,
      sharedRates, // Include for debugging/verification purposes
      warning, // Include warning if depletion ages don't match
    };
  }

  /**
   * Get the original Monte Carlo result for a specific iteration
   * This returns the authoritative data from the original run, avoiding re-simulation issues
   */
  getStoredIterationResult(iteration: number): CombinedMonteCarloResult | undefined {
    return this.storedResults.find(result => result.iteration === iteration);
  }

  /**
   * Get all stored Monte Carlo results
   */
  getStoredResults(): CombinedMonteCarloResult[] {
    return [...this.storedResults]; // Return copy to prevent mutation
  }

  /**
   * Run a single 1-Topf iteration with shared rates
   */
  private async runSingle1TopfIteration(
    _iteration: number, // Unused for now
    entnahmeJahre: number,
    sharedRates: any
  ): Promise<{
    finalBalance: number;
    totalWithdrawals: number;
    successfulStrategy: boolean;
    yearlyBalances: number[];
    kapitalIndex: number[];
    jahresergebnisse: import('../types').OneTopfYearData[];
  }> {
    // CRITICAL FIX: Create a new OneTopfSimulationEngine instance for each iteration
    // This ensures clean state and proper use of shared rates for GARCH sequences
    const simulationEngine = new OneTopfSimulationEngine(this.steuerParameter, this.oneTopfOptions, this.crisisDefinition);
    simulationEngine.setSharedRates(sharedRates);

    // Run simulation
    const results = await simulationEngine.runSimulation();
    const lastYear = results[results.length - 1];

    // Calculate metrics
    const totalWithdrawals = results.reduce((sum, year) => sum + year.gesamtentnahme, 0);
    const finalBalance = lastYear ? lastYear.gesamtsaldo : 0;
    
    // SUCCESS DEFINITION (verfeinert, zentral in isStrategySuccessful):
    // A portfolio is SUCCESSFUL if it (1) reaches the target age
    // (jahresergebnisse.length >= entnahmeJahre + 1) AND (2) serves at least
    // SUCCESS_WITHDRAWAL_THRESHOLD of the inflation-adjusted target withdrawal in the
    // TARGET YEAR (last entry: gesamtentnahme / zielentnahme >= threshold).
    //
    // Boundary: reaching the target age with a FULL target-year withdrawal but an end
    // balance of 0 is SUCCESS (100 % served). Reaching the target age but only serving a
    // PARTIAL withdrawal there (balance depleted) is now FAILURE ("Zieljahr unterfinanziert").
    const successfulStrategy = isStrategySuccessful(results, entnahmeJahre);

    // Capture yearly total balances (sum of all Töpfe)
    const yearlyBalances = results.map(year => year.gesamtsaldo);

    // Use actual Kapitalindex from simulation results (ensures consistency with Einzelsimulation)
    const kapitalIndex = results.map(year => {
      // Use the actual kapitalIndex from simulation data, with fallback for backward compatibility
      if (year.kapitalIndex !== undefined) {
        return Math.round(year.kapitalIndex);
      }
      
      // Fallback: Calculate if not available (should not happen with current implementation)
      console.warn('Monte Carlo 1-Topf: kapitalIndex missing from simulation result, using fallback calculation');
      return 100; // Safe default
    });

    return {
      finalBalance,
      totalWithdrawals,
      successfulStrategy,
      yearlyBalances,
      kapitalIndex,
      jahresergebnisse: results, // SINGLE SOURCE OF TRUTH - all other metrics derived from this
    };
  }

  /**
   * Calculate failure analysis for a single iteration
   * Uses jahresergebnisse as SINGLE SOURCE OF TRUTH
   */
  private calculateFailureAnalysis(
    _iteration: number,
    threeTopfResult: any,
    oneTopfResult: any
  ): {
    threeTopfFailedFirst: boolean;
    oneTopfFailedFirst: boolean;
    yearsAdvantage: number;
    earlyFailureType: 'none' | '3-topf-early' | '1-topf-early';
  } {
    const threeTopfFailed = !threeTopfResult.successfulStrategy;
    const oneTopfFailed = !oneTopfResult.successfulStrategy;
    
    let threeTopfFailedFirst = false;
    let oneTopfFailedFirst = false;
    let yearsAdvantage = 0;
    let earlyFailureType: 'none' | '3-topf-early' | '1-topf-early' = 'none';

    if (threeTopfFailed && oneTopfFailed) {
      // Both failed - compare depletion ages using ACTUAL alter field from last year
      const threeTopfDepletionAge = threeTopfResult.jahresergebnisse[threeTopfResult.jahresergebnisse.length - 1].alter;
      const oneTopfDepletionAge = oneTopfResult.jahresergebnisse[oneTopfResult.jahresergebnisse.length - 1].alter;
      
      if (threeTopfDepletionAge < oneTopfDepletionAge) {
        threeTopfFailedFirst = true;
        yearsAdvantage = oneTopfDepletionAge - threeTopfDepletionAge;
        earlyFailureType = '3-topf-early';
      } else if (oneTopfDepletionAge < threeTopfDepletionAge) {
        oneTopfFailedFirst = true;
        yearsAdvantage = threeTopfDepletionAge - oneTopfDepletionAge;
        earlyFailureType = '1-topf-early';
      }
    } else if (threeTopfFailed && !oneTopfFailed) {
      // Only 3-Topf failed
      threeTopfFailedFirst = true;
      const threeTopfDepletionAge = threeTopfResult.jahresergebnisse[threeTopfResult.jahresergebnisse.length - 1].alter;
      yearsAdvantage = this.steuerParameter.berechnungBisLebensalter - threeTopfDepletionAge;
      earlyFailureType = '3-topf-early';
    } else if (!threeTopfFailed && oneTopfFailed) {
      // Only 1-Topf failed
      oneTopfFailedFirst = true;
      const oneTopfDepletionAge = oneTopfResult.jahresergebnisse[oneTopfResult.jahresergebnisse.length - 1].alter;
      yearsAdvantage = this.steuerParameter.berechnungBisLebensalter - oneTopfDepletionAge;
      earlyFailureType = '1-topf-early';
    }

    return {
      threeTopfFailedFirst,
      oneTopfFailedFirst,
      yearsAdvantage,
      earlyFailureType,
    };
  }

  /**
   * Calculate combined statistics from results
   */
  calculateCombinedStatistics(results: CombinedMonteCarloResult[]): CombinedMonteCarloStats {
    if (results.length === 0) {
      throw new Error('No results to calculate statistics from');
    }

    // Use consolidated statistical helpers
    const entnahmeJahre = this.steuerParameter.berechnungBisLebensalter - this.steuerParameter.retirementAge;

    // Extract data arrays - use jahresergebnisse.length as SINGLE SOURCE OF TRUTH
    // yearsFunded = jahresergebnisse.length - 1: Jahr 0 (Rentenbeginn) zählt nicht als
    // finanziertes Jahr NACH Rentenbeginn. Ein voller Lauf über entnahmeJahre + 1 Einträge
    // entspricht damit genau entnahmeJahre finanzierten Jahren.
    // Wenn das Zialjahr unterfinanziert ist (Ratio < Schwelle), zähle es anteilig:
    // yearsFunded = (length - 2) + ratio statt length - 1
    const threeTopfBalances = results.map(r => r.threeTopf.finalBalance);
    const threeTopfYears = results.map(r => {
      const baseYears = r.threeTopf.jahresergebnisse.length - 1;
      const ratio = getTargetYearFundingRatio(r.threeTopf.jahresergebnisse, entnahmeJahre);
      // Wenn das Zieljahr unterfinanziert ist und nicht null, zähle anteilig
      if (ratio !== null && ratio < 1) {
        return Math.max(0, (r.threeTopf.jahresergebnisse.length - 2) + ratio);
      }
      return baseYears;
    });
    const threeTopfSuccesses = results.filter(r => r.threeTopf.successfulStrategy).length;

    const oneTopfBalances = results.map(r => r.oneTopf.finalBalance);
    const oneTopfYears = results.map(r => {
      const baseYears = r.oneTopf.jahresergebnisse.length - 1;
      const ratio = getTargetYearFundingRatio(r.oneTopf.jahresergebnisse, entnahmeJahre);
      // Wenn das Zieljahr unterfinanziert ist und nicht null, zähle anteilig
      if (ratio !== null && ratio < 1) {
        return Math.max(0, (r.oneTopf.jahresergebnisse.length - 2) + ratio);
      }
      return baseYears;
    });
    const oneTopfSuccesses = results.filter(r => r.oneTopf.successfulStrategy).length;

    // Calculate comparison metrics
    const threeTopfAdvantageCount = results.filter(
      r => r.threeTopf.finalBalance > r.oneTopf.finalBalance
    ).length;
    const oneTopfAdvantageCount = results.filter(
      r => r.oneTopf.finalBalance > r.threeTopf.finalBalance
    ).length;

    const balanceDifferences = results.map(r => r.threeTopf.finalBalance - r.oneTopf.finalBalance);

    return {
      iterations: results.length,

      threeTopf: {
        averageFinalBalance: StatisticalHelpers.calculateMean(threeTopfBalances),
        medianFinalBalance: StatisticalHelpers.calculateMedian(threeTopfBalances),
        averageYearsFunded: StatisticalHelpers.calculateMean(threeTopfYears),
        successRate: (threeTopfSuccesses / results.length) * 100,
        percentile5: StatisticalHelpers.calculatePercentile(threeTopfBalances, 5),
        percentile95: StatisticalHelpers.calculatePercentile(threeTopfBalances, 95),
      },

      oneTopf: {
        averageFinalBalance: StatisticalHelpers.calculateMean(oneTopfBalances),
        medianFinalBalance: StatisticalHelpers.calculateMedian(oneTopfBalances),
        averageYearsFunded: StatisticalHelpers.calculateMean(oneTopfYears),
        successRate: (oneTopfSuccesses / results.length) * 100,
        percentile5: StatisticalHelpers.calculatePercentile(oneTopfBalances, 5),
        percentile95: StatisticalHelpers.calculatePercentile(oneTopfBalances, 95),
      },

      comparison: {
        threeTopfAdvantageRate: (threeTopfAdvantageCount / results.length) * 100,
        oneTopfAdvantageRate: (oneTopfAdvantageCount / results.length) * 100,
        averageBalanceDifference: StatisticalHelpers.calculateMean(balanceDifferences),
        bestCombinedStrategy: threeTopfAdvantageCount > oneTopfAdvantageCount ? '3-Topf' : '1-Topf',
      },

      failureAnalysis: this.calculateFailureStatistics(results),
    };
  }

  /**
   * Calculate failure analysis statistics
   */
  private calculateFailureStatistics(results: CombinedMonteCarloResult[]): {
    threeTopfEarlyFailureRate: number;
    oneTopfEarlyFailureRate: number;
    bothFailedRate: number;
    averageYearsAdvantage: number;
    worstCaseScenarios: {
      threeTopfWorst: CombinedMonteCarloResult[];
      oneTopfWorst: CombinedMonteCarloResult[];
    };
    criticalFailureThreshold: number;
    failureComparison: {
      threeTopfCriticalFailures: number;
      oneTopfCriticalFailures: number;
      maxYearsAdvantage: {
        threeTopf: number;
        oneTopf: number;
      };
      averageYearsAdvantage: {
        threeTopf: number;
        oneTopf: number;
      };
    };
  } {
    const threeTopfEarlyFailures = results.filter(r => r.failureAnalysis.threeTopfFailedFirst);
    const oneTopfEarlyFailures = results.filter(r => r.failureAnalysis.oneTopfFailedFirst);
    const bothFailedResults = results.filter(r => 
      !r.threeTopf.successfulStrategy && !r.oneTopf.successfulStrategy
    );

    // Calculate rates
    const threeTopfEarlyFailureRate = (threeTopfEarlyFailures.length / results.length) * 100;
    const oneTopfEarlyFailureRate = (oneTopfEarlyFailures.length / results.length) * 100;
    const bothFailedRate = (bothFailedResults.length / results.length) * 100;

    // Calculate average years advantage
    const yearsAdvantages = results
      .filter(r => r.failureAnalysis.yearsAdvantage > 0)
      .map(r => r.failureAnalysis.yearsAdvantage);
    const averageYearsAdvantage = yearsAdvantages.length > 0 
      ? StatisticalHelpers.calculateMean(yearsAdvantages) : 0;

    // Find worst case scenarios for both strategies
    const threeTopfWorstScenarios = threeTopfEarlyFailures
      .sort((a, b) => b.failureAnalysis.yearsAdvantage - a.failureAnalysis.yearsAdvantage)
      .slice(0, 10); // Top 10 worst 3-Topf cases

    const oneTopfWorstScenarios = oneTopfEarlyFailures
      .sort((a, b) => b.failureAnalysis.yearsAdvantage - a.failureAnalysis.yearsAdvantage)
      .slice(0, 10); // Top 10 worst 1-Topf cases
    
    // Define critical failure threshold (5+ years difference)
    const criticalFailureThreshold = 5;

    // Count critical failures (beyond threshold)
    const threeTopfCriticalFailures = threeTopfEarlyFailures
      .filter(r => r.failureAnalysis.yearsAdvantage >= criticalFailureThreshold).length;
    const oneTopfCriticalFailures = oneTopfEarlyFailures
      .filter(r => r.failureAnalysis.yearsAdvantage >= criticalFailureThreshold).length;

    // Calculate maximum and average advantages per strategy
    // CRITICAL FIX: Use yearsFunded/jahresergebnisse.length to determine depletion
    // instead of checking depletionAge field which can be corrupted
    // Only consider cases where BOTH portfolios actually depleted
    
    const targetYears = this.steuerParameter.berechnungBisLebensalter - this.steuerParameter.retirementAge;

    // Hinweis: threeTopfDepleted (length <= targetYears) misst hier ausschließlich die
    // Erschöpfungs-ZEIT für den yearsAdvantage-Vergleich (wer früher erschöpft war).
    // Es ist NICHT mehr das exakte Komplement von successfulStrategy: ein Lauf, der das
    // Zielalter erreicht (length == targetYears + 1), dort aber die 95 %-Schwelle reißt,
    // ist ein Failure ("Zieljahr unterfinanziert"), hat aber keinen Zeit-Vorsprung und
    // wird für den yearsAdvantage-Vergleich korrekt als "nicht früher erschöpft" behandelt.

    // Calculate 3-Topf advantages (when 1-Topf failed first)
    // Only count when BOTH portfolios depleted
    // Use jahresergebnisse.length as SINGLE SOURCE OF TRUTH
    const threeTopfAdvantages = results
      .map(r => {
        const threeTopfYears = r.threeTopf.jahresergebnisse.length;
        const oneTopfYears = r.oneTopf.jahresergebnisse.length;
        
        // Only consider cases where both actually depleted (didn't reach target years)
        const threeTopfDepleted = threeTopfYears <= targetYears;
        const oneTopfDepleted = oneTopfYears <= targetYears;
        
        if (!threeTopfDepleted || !oneTopfDepleted) {
          return null;
        }
        
        // Only count when 1-Topf failed first (lasted fewer years)
        return oneTopfYears < threeTopfYears ? threeTopfYears - oneTopfYears : null;
      })
      .filter(adv => adv !== null && adv > 0) as number[];
    
    // Calculate 1-Topf advantages (when 3-Topf failed first)
    // Only count when BOTH portfolios depleted
    // Use jahresergebnisse.length as SINGLE SOURCE OF TRUTH
    const oneTopfAdvantages = results
      .map(r => {
        const threeTopfYears = r.threeTopf.jahresergebnisse.length;
        const oneTopfYears = r.oneTopf.jahresergebnisse.length;
        
        // Only consider cases where both actually depleted (didn't reach target years)
        const threeTopfDepleted = threeTopfYears <= targetYears;
        const oneTopfDepleted = oneTopfYears <= targetYears;
        
        if (!threeTopfDepleted || !oneTopfDepleted) {
          return null;
        }
        
        // Only count when 3-Topf failed first (lasted fewer years)
        return threeTopfYears < oneTopfYears ? oneTopfYears - threeTopfYears : null;
      })
      .filter(adv => adv !== null && adv > 0) as number[];

    const maxThreeTopfAdvantage = threeTopfAdvantages.length > 0 
      ? Math.max(...threeTopfAdvantages) : 0;
    const maxOneTopfAdvantage = oneTopfAdvantages.length > 0 
      ? Math.max(...oneTopfAdvantages) : 0;
    
    // v2.4.9: Calculate average advantage per strategy for detailed breakdown
    const avgThreeTopfAdvantage = threeTopfAdvantages.length > 0
      ? StatisticalHelpers.calculateMean(threeTopfAdvantages) : 0;
    const avgOneTopfAdvantage = oneTopfAdvantages.length > 0
      ? StatisticalHelpers.calculateMean(oneTopfAdvantages) : 0;

    return {
      threeTopfEarlyFailureRate,
      oneTopfEarlyFailureRate,
      bothFailedRate,
      averageYearsAdvantage,
      worstCaseScenarios: {
        threeTopfWorst: threeTopfWorstScenarios,
        oneTopfWorst: oneTopfWorstScenarios,
      },
      criticalFailureThreshold,
      failureComparison: {
        threeTopfCriticalFailures,
        oneTopfCriticalFailures,
        maxYearsAdvantage: {
          threeTopf: maxThreeTopfAdvantage,
          oneTopf: maxOneTopfAdvantage,
        },
        averageYearsAdvantage: {
          threeTopf: avgThreeTopfAdvantage,
          oneTopf: avgOneTopfAdvantage,
        },
      },
    };
  }

  /**
   * Run Zinstopf size comparison analysis
   * Executes Monte Carlo simulations across different anzahlJahreTopf2 values
   * to find the optimal configuration
   * 
   * @param iterations - Number of Monte Carlo iterations per configuration
   * @param config - Configuration defining the range of years to test
   * @param progressCallback - Optional callback for progress updates (0-100)
   * @returns Array of comparison results for each configuration (includes raw results for client-side filtering)
   */
  async runZinstopfSizeComparison(
    iterations: number,
    config: ZinstopfComparisonConfig,
    progressCallback?: (progress: number, currentYears?: number, totalYears?: number) => void
  ): Promise<ZinstopfComparisonResult[]> {
    const results: ZinstopfComparisonResult[] = [];
    const yearRange = [];
    
    // Generate array of year values to test based on config
    for (let years = config.minYears; years <= config.maxYears; years += config.stepSize) {
      yearRange.push(years);
    }
    
    const totalConfigurations = yearRange.length;

    // Run Monte Carlo for each configuration
    for (let configIndex = 0; configIndex < yearRange.length; configIndex++) {
      const anzahlJahreTopf2 = yearRange[configIndex];
      
      // Create temporary parameters with modified anzahlJahreTopf2
      const tempParams: SteuerParameter = {
        ...this.steuerParameter,
        anzahlJahreTopf2,
      };
      
      // Create temporary engine instance with modified parameters
      const tempEngine = new CombinedMonteCarloEngine(
        tempParams,
        this.threeTopfOptions,
        this.oneTopfOptions,
        this.crisisDefinition
      );
      
      // Run Monte Carlo simulation for this configuration
      // MEMORY OPTIMIZATION: Don't store iteration details during Zinstopf optimization
      // This prevents out-of-memory crashes when running 10+ configs × 1000 iterations
      const mcResults = await tempEngine.runCombinedMonteCarlo(
        iterations,
        (iterProgress) => {
          // Calculate overall progress
          const configProgress = (configIndex / totalConfigurations) * 100;
          const currentIterProgress = (iterProgress / totalConfigurations);
          const totalProgress = Math.min(100, configProgress + currentIterProgress);
          
          if (progressCallback) {
            progressCallback(totalProgress, anzahlJahreTopf2, totalConfigurations);
          }
        },
        false // Don't store iteration details to save memory
      );
      
      // Calculate statistics
      const stats = tempEngine.calculateCombinedStatistics(mcResults);
      
      // Find best and worst scenarios
      const sortedByThreeTopf = [...mcResults].sort(
        (a, b) => b.threeTopf.finalBalance - a.threeTopf.finalBalance
      );
      
      const best = sortedByThreeTopf[0];
      const worst = sortedByThreeTopf[sortedByThreeTopf.length - 1];
      
      // Calculate detailed failure metrics before discarding rawResults
      const failures = mcResults.filter(r => !r.threeTopf.successfulStrategy);
      const totalIterations = mcResults.length;
      let avgYearsToFailure = 0;
      let medianYearsToFailure = 0;
      let worstCaseYear = 0;
      let bestCaseYear = 0;
      
      if (failures.length > 0) {
        const failureYears = failures.map(f => f.threeTopf.jahresergebnisse.length - 1);
        avgYearsToFailure = failureYears.reduce((sum, y) => sum + y, 0) / failures.length;
        
        // Calculate median
        const sortedYears = [...failureYears].sort((a, b) => a - b);
        const mid = Math.floor(sortedYears.length / 2);
        medianYearsToFailure = sortedYears.length % 2 === 0
          ? (sortedYears[mid - 1] + sortedYears[mid]) / 2
          : sortedYears[mid];
        
        worstCaseYear = Math.min(...failureYears);
        bestCaseYear = Math.max(...failureYears);
      }
      
      // Extract key statistics for comparison
      // MEMORY OPTIMIZATION: Don't store rawResults - they're not needed for Zinstopf optimization
      const comparisonResult: ZinstopfComparisonResult = {
        anzahlJahreTopf2,
        rawResults: [], // Empty array to save memory - filtering not needed for optimization
        statistics: {
          threeTopfSuccessRate: stats.threeTopf.successRate,
          threeTopfAvgFinalBalance: stats.threeTopf.averageFinalBalance,
          threeTopfMedianFinalBalance: stats.threeTopf.medianFinalBalance,
          oneTopfSuccessRate: stats.oneTopf.successRate,
          oneTopfAvgFinalBalance: stats.oneTopf.averageFinalBalance,
          oneTopfMedianFinalBalance: stats.oneTopf.medianFinalBalance,
          threeTopfAdvantageRate: stats.comparison.threeTopfAdvantageRate,
          averageYearsAdvantage: stats.failureAnalysis.averageYearsAdvantage,
          failureRate: 100 - stats.threeTopf.successRate,
        },
        bestScenario: {
          iteration: best.iteration,
          threeTopfFinalBalance: best.threeTopf.finalBalance,
          oneTopfFinalBalance: best.oneTopf.finalBalance,
        },
        worstScenario: {
          iteration: worst.iteration,
          threeTopfFinalBalance: worst.threeTopf.finalBalance,
          oneTopfFinalBalance: worst.oneTopf.finalBalance,
        },
        failureMetrics: {
          totalFailures: failures.length,
          totalIterations: totalIterations,
          avgYearsToFailure: avgYearsToFailure,
          medianYearsToFailure: medianYearsToFailure,
          worstCaseYear: worstCaseYear,
          bestCaseYear: bestCaseYear,
        },
      };
      
      results.push(comparisonResult);
    }
    
    // Final progress update
    if (progressCallback) {
      progressCallback(100, undefined, totalConfigurations);
    }
    
    return results;
  }

  /**
   * Run withdrawal rate comparison analysis
   * Tests different portfolioEntnahmeRate values to find optimal withdrawal rate
   * MEMORY OPTIMIZATION: Does not store iteration details to prevent out-of-memory crashes
   */
  async runWithdrawalRateComparison(
    iterations: number,
    config: WithdrawalRateComparisonConfig,
    progressCallback?: (progress: number, currentRate?: number, totalRates?: number) => void
  ): Promise<WithdrawalRateComparisonResult[]> {
    const results: WithdrawalRateComparisonResult[] = [];
    const rateRange = [];
    
    // Generate array of rate values to test based on config
    // Ganzzahlige Schrittzählung statt "rate += stepSize", um Gleitkomma-Akkumulation zu
    // vermeiden (sonst kann der Maximalwert aus der Testreihe fallen).
    const rateSteps = Math.floor((config.maxRate - config.minRate) / config.stepSize + 1e-9);
    for (let k = 0; k <= rateSteps; k++) {
      const rate = config.minRate + k * config.stepSize;
      rateRange.push(Math.round(rate * 100) / 100); // Round to 2 decimals
    }
    
    const totalConfigurations = rateRange.length;

    // Run Monte Carlo for each configuration
    for (let configIndex = 0; configIndex < rateRange.length; configIndex++) {
      const withdrawalRate = rateRange[configIndex];
      
      // Create temporary parameters with modified portfolioEntnahmeRate
      const tempParams: SteuerParameter = {
        ...this.steuerParameter,
        portfolioEntnahmeRate: withdrawalRate,
      };
      
      // Create temporary engine instance with modified parameters
      const tempEngine = new CombinedMonteCarloEngine(
        tempParams,
        this.threeTopfOptions,
        this.oneTopfOptions,
        this.crisisDefinition
      );
      
      // Run Monte Carlo simulation for this configuration
      // MEMORY OPTIMIZATION: Don't store iteration details during withdrawal rate optimization
      // This prevents out-of-memory crashes when running 10+ configs × 1000 iterations
      const mcResults = await tempEngine.runCombinedMonteCarlo(
        iterations,
        (iterProgress) => {
          // Calculate overall progress
          const configProgress = (configIndex / totalConfigurations) * 100;
          const currentIterProgress = (iterProgress / totalConfigurations);
          const totalProgress = Math.min(100, configProgress + currentIterProgress);
          
          if (progressCallback) {
            progressCallback(totalProgress, withdrawalRate, totalConfigurations);
          }
        },
        false // Don't store iteration details to save memory
      );
      
      // Calculate statistics
      const stats = tempEngine.calculateCombinedStatistics(mcResults);
      
      // Find best and worst scenarios
      const sortedByThreeTopf = [...mcResults].sort(
        (a, b) => b.threeTopf.finalBalance - a.threeTopf.finalBalance
      );
      
      const best = sortedByThreeTopf[0];
      const worst = sortedByThreeTopf[sortedByThreeTopf.length - 1];
      
      // Calculate detailed failure metrics before discarding rawResults
      const failures = mcResults.filter(r => !r.threeTopf.successfulStrategy);
      const totalIterations = mcResults.length;
      let avgYearsToFailure = 0;
      let medianYearsToFailure = 0;
      let worstCaseYear = 0;
      let bestCaseYear = 0;
      
      if (failures.length > 0) {
        const failureYears = failures.map(f => f.threeTopf.jahresergebnisse.length - 1);
        avgYearsToFailure = failureYears.reduce((sum, y) => sum + y, 0) / failures.length;
        
        // Calculate median
        const sortedYears = [...failureYears].sort((a, b) => a - b);
        const mid = Math.floor(sortedYears.length / 2);
        medianYearsToFailure = sortedYears.length % 2 === 0
          ? (sortedYears[mid - 1] + sortedYears[mid]) / 2
          : sortedYears[mid];
        
        worstCaseYear = Math.min(...failureYears);
        bestCaseYear = Math.max(...failureYears);
      }
      
      // Extract key statistics for comparison
      // MEMORY OPTIMIZATION: Don't store rawResults - they're not needed for withdrawal rate optimization
      const comparisonResult: WithdrawalRateComparisonResult = {
        withdrawalRate,
        rawResults: [], // Empty array to save memory - filtering not needed for optimization
        statistics: {
          threeTopfSuccessRate: stats.threeTopf.successRate,
          threeTopfAvgFinalBalance: stats.threeTopf.averageFinalBalance,
          threeTopfMedianFinalBalance: stats.threeTopf.medianFinalBalance,
          oneTopfSuccessRate: stats.oneTopf.successRate,
          oneTopfAvgFinalBalance: stats.oneTopf.averageFinalBalance,
          oneTopfMedianFinalBalance: stats.oneTopf.medianFinalBalance,
          threeTopfAdvantageRate: stats.comparison.threeTopfAdvantageRate,
          averageYearsAdvantage: stats.failureAnalysis.averageYearsAdvantage,
          failureRate: 100 - stats.threeTopf.successRate,
        },
        bestScenario: {
          iteration: best.iteration,
          threeTopfFinalBalance: best.threeTopf.finalBalance,
          oneTopfFinalBalance: best.oneTopf.finalBalance,
        },
        worstScenario: {
          iteration: worst.iteration,
          threeTopfFinalBalance: worst.threeTopf.finalBalance,
          oneTopfFinalBalance: worst.oneTopf.finalBalance,
        },
        failureMetrics: {
          totalFailures: failures.length,
          totalIterations: totalIterations,
          avgYearsToFailure: avgYearsToFailure,
          medianYearsToFailure: medianYearsToFailure,
          worstCaseYear: worstCaseYear,
          bestCaseYear: bestCaseYear,
        },
      };
      
      results.push(comparisonResult);
    }
    
    // Final progress update
    if (progressCallback) {
      progressCallback(100, undefined, totalConfigurations);
    }
    
    return results;
  }

  /**
   * Run combined optimization analysis (Zinstopf + Withdrawal Rate)
   * Tests all combinations of Zinstopf sizes and withdrawal rates in a 2D grid
   * MEMORY OPTIMIZATION: Does not store iteration details to prevent out-of-memory crashes
   */
  async runCombinedOptimization(
    iterations: number,
    config: CombinedOptimizationConfig,
    progressCallback?: (
      progress: number, 
      currentZinstopf?: number, 
      currentRate?: number,
      totalConfigs?: number
    ) => void
  ): Promise<CombinedOptimizationResult[]> {
    const results: CombinedOptimizationResult[] = [];
    
    // Generate ranges
    const zinstopfRange = [];
    for (let y = config.minZinstopfYears; y <= config.maxZinstopfYears; y += config.zinstopfStepSize) {
      zinstopfRange.push(y);
    }
    
    const rateRange = [];
    // Ganzzahlige Schrittzählung statt "r += stepSize", um Gleitkomma-Akkumulation zu
    // vermeiden (sonst kann der Maximalwert aus der Testreihe fallen).
    const withdrawalSteps = Math.floor(
      (config.maxWithdrawalRate - config.minWithdrawalRate) / config.withdrawalStepSize + 1e-9
    );
    for (let k = 0; k <= withdrawalSteps; k++) {
      const r = config.minWithdrawalRate + k * config.withdrawalStepSize;
      rateRange.push(Math.round(r * 100) / 100);
    }
    
    const totalConfigurations = zinstopfRange.length * rateRange.length;
    let completedConfigs = 0;
    
    // Nested loop: for each Zinstopf size, test all withdrawal rates
    for (const zinstopfYears of zinstopfRange) {
      for (const withdrawalRate of rateRange) {
        // Create temporary parameters
        const tempParams: SteuerParameter = {
          ...this.steuerParameter,
          anzahlJahreTopf2: zinstopfYears,
          portfolioEntnahmeRate: withdrawalRate,
        };
        
        // Create temporary engine
        const tempEngine = new CombinedMonteCarloEngine(
          tempParams,
          this.threeTopfOptions,
          this.oneTopfOptions,
          this.crisisDefinition
        );
        
        // Run Monte Carlo (without storing iteration details)
        const mcResults = await tempEngine.runCombinedMonteCarlo(
          iterations,
          (iterProgress) => {
            const configProgress = (completedConfigs / totalConfigurations) * 100;
            const currentIterProgress = (iterProgress / totalConfigurations);
            const totalProgress = Math.min(100, configProgress + currentIterProgress);
            
            if (progressCallback) {
              progressCallback(totalProgress, zinstopfYears, withdrawalRate, totalConfigurations);
            }
          },
          false // Don't store iteration details
        );
        
        // Calculate statistics and metrics
        const stats = tempEngine.calculateCombinedStatistics(mcResults);
        const sortedByThreeTopf = [...mcResults].sort(
          (a, b) => b.threeTopf.finalBalance - a.threeTopf.finalBalance
        );
        const best = sortedByThreeTopf[0];
        const worst = sortedByThreeTopf[sortedByThreeTopf.length - 1];
        
        // Calculate failure metrics
        const failures = mcResults.filter(r => !r.threeTopf.successfulStrategy);
        const totalIterations = mcResults.length;
        let avgYearsToFailure = 0;
        let medianYearsToFailure = 0;
        let worstCaseYear = 0;
        let bestCaseYear = 0;
        
        if (failures.length > 0) {
          const failureYears = failures.map(f => f.threeTopf.jahresergebnisse.length - 1);
          avgYearsToFailure = failureYears.reduce((sum, y) => sum + y, 0) / failures.length;
          const sortedYears = [...failureYears].sort((a, b) => a - b);
          const mid = Math.floor(sortedYears.length / 2);
          medianYearsToFailure = sortedYears.length % 2 === 0
            ? (sortedYears[mid - 1] + sortedYears[mid]) / 2
            : sortedYears[mid];
          worstCaseYear = Math.min(...failureYears);
          bestCaseYear = Math.max(...failureYears);
        }
        
        results.push({
          zinstopfYears,
          withdrawalRate,
          statistics: {
            threeTopfSuccessRate: stats.threeTopf.successRate,
            threeTopfAvgFinalBalance: stats.threeTopf.averageFinalBalance,
            threeTopfMedianFinalBalance: stats.threeTopf.medianFinalBalance,
            oneTopfSuccessRate: stats.oneTopf.successRate,
            oneTopfAvgFinalBalance: stats.oneTopf.averageFinalBalance,
            oneTopfMedianFinalBalance: stats.oneTopf.medianFinalBalance,
            threeTopfAdvantageRate: stats.comparison.threeTopfAdvantageRate,
            averageYearsAdvantage: stats.failureAnalysis.averageYearsAdvantage,
            failureRate: 100 - stats.threeTopf.successRate,
          },
          bestScenario: {
            iteration: best.iteration,
            threeTopfFinalBalance: best.threeTopf.finalBalance,
            oneTopfFinalBalance: best.oneTopf.finalBalance,
          },
          worstScenario: {
            iteration: worst.iteration,
            threeTopfFinalBalance: worst.threeTopf.finalBalance,
            oneTopfFinalBalance: worst.oneTopf.finalBalance,
          },
          failureMetrics: {
            totalFailures: failures.length,
            totalIterations: totalIterations,
            avgYearsToFailure: avgYearsToFailure,
            medianYearsToFailure: medianYearsToFailure,
            worstCaseYear: worstCaseYear,
            bestCaseYear: bestCaseYear,
          },
        });
        
        completedConfigs++;
      }
    }
    
    if (progressCallback) {
      progressCallback(100, undefined, undefined, totalConfigurations);
    }

    return results;
  }

  /**
   * Run the full scenario-matrix comparison („Wann gewinnt der 3-Topf?").
   *
   * Rechnet die volle Matrix aus Inflations- × Rendite-Optionen × Entnahmeraten-
   * Sweep durch und vergleicht 3-Topf vs. 1-Topf je Zelle auf TAIL-Metriken
   * (P5/P10-Endkapital mit Verarmt-⇒-0-Regel, Verarmungsrisiko) statt Mittelwerten.
   *
   * Fairness: Pro Zelle teilen 3-Topf und 1-Topf über EINE Temp-Engine identische
   * Raten-/Marktpfade (SharedRateGenerator, seed = Iterationsindex). Die Markt-Seeds
   * sind entnahmeraten-UNABHÄNGIG — nur portfolioEntnahmeRate variiert pro Rate-Zelle,
   * die Marktszenarien bleiben vergleichbar. Tagesgeld-/Zinstopf-Optionen bleiben wie
   * vom Nutzer gewählt; nur Inflations- und Rendite-Option der Investitionsseite
   * werden pro Zelle variiert (für 3-Topf und 1-Topf identisch).
   *
   * MEMORY: runCombinedMonteCarlo(..., false) speichert keine Iterationsdetails; die
   * Roh-Ergebnisse (mcResults) fallen pro Zelle aus dem Scope → flacher Speicher.
   *
   * @param config - Raten-Range/Step, Iterationen pro Zelle, optionale Options-Overrides (Tests)
   * @param progressCallback - (progress 0–100, currentCellLabel?, cellIndex?, totalCells?)
   * @returns Array von Zell-Ergebnissen (eine Zeile je Inflations-/Rendite-/Raten-Kombination)
   */
  async runScenarioMatrixComparison(
    config: ScenarioMatrixConfig,
    progressCallback?: (
      progress: number,
      currentCellLabel?: string,
      cellIndex?: number,
      totalCells?: number
    ) => void
  ): Promise<ScenarioCellResult[]> {
    const results: ScenarioCellResult[] = [];

    // Options-Listen (Test-Override möglich).
    const inflationOptions = config.inflationOptions ?? INFLATION_OPTIONS;
    const investmentReturnOptions = config.investmentReturnOptions ?? INVESTMENT_RETURN_OPTIONS;

    // Raten-Range mit ganzzahliger Schrittzählung (kein Gleitkomma-Drift, identisch
    // zum Muster in runWithdrawalRateComparison).
    const rateRange: number[] = [];
    const rateSteps = Math.floor((config.maxRate - config.minRate) / config.stepSize + 1e-9);
    for (let k = 0; k <= rateSteps; k++) {
      const rate = config.minRate + k * config.stepSize;
      rateRange.push(Math.round(rate * 100) / 100);
    }

    const totalCells =
      investmentReturnOptions.length * inflationOptions.length * rateRange.length;
    let completedCells = 0;

    // Zell-Reihenfolge: Rendite-Option außen → Inflation → Rate. So werden GARCH-Zellen
    // zusammenhängend abgearbeitet und der Kalibrierungs-Cache muss nur einmal warmlaufen.
    for (const investmentReturnOption of investmentReturnOptions) {
      for (const inflationOption of inflationOptions) {
        for (const withdrawalRate of rateRange) {
          const cellLabel =
            `Rendite ${investmentReturnOption}, Inflation ${inflationOption}, ` +
            `Rate ${withdrawalRate}%`;

          // Temp-Parameter mit variierender Entnahmerate.
          const tempParams: SteuerParameter = {
            ...this.steuerParameter,
            portfolioEntnahmeRate: withdrawalRate,
          };

          // 3-Topf-Optionen: Inflations-/Rendite-Option der Zelle; Tagesgeld/Zinstopf
          // bleiben wie gewählt.
          const tempThreeTopfOptions: SimulationOptions = {
            ...this.threeTopfOptions,
            inflationOption,
            investmentReturnOption,
          };
          // 1-Topf-Optionen: gleiche Inflations-/Rendite-Option (fairer Vergleich).
          const tempOneTopfOptions: OneTopfSimulationOptions = {
            ...this.oneTopfOptions,
            inflationOption,
            investmentReturnOption,
          };

          const tempEngine = new CombinedMonteCarloEngine(
            tempParams,
            tempThreeTopfOptions,
            tempOneTopfOptions,
            this.crisisDefinition
          );

          const localCompleted = completedCells;
          const mcResults = await tempEngine.runCombinedMonteCarlo(
            config.iterationsPerCell,
            (iterProgress) => {
              const cellProgress = (localCompleted / totalCells) * 100;
              const currentIterProgress = iterProgress / totalCells;
              const totalProgress = Math.min(100, cellProgress + currentIterProgress);
              if (progressCallback) {
                progressCallback(totalProgress, cellLabel, localCompleted + 1, totalCells);
              }
            },
            false // keine Iterationsdetails → Speicher flach
          );

          // Tail-Metriken + Verdict über die puren Helfer. calculateCombinedStatistics
          // wird bewusst NICHT wiederverwendet (implementiert die Verarmt-⇒-0-Regel nicht).
          results.push(
            buildScenarioCellResult(
              mcResults,
              inflationOption,
              investmentReturnOption,
              withdrawalRate
            )
          );

          completedCells++;
        }
      }
    }

    if (progressCallback) {
      progressCallback(100, undefined, totalCells, totalCells);
    }

    return results;
  }
}
